'use client'

import { Provider, useDispatch, useSelector } from 'react-redux'
import type { AppProps } from 'next/app'
import { persistor, store } from '../store/configureStore'
import '../styles/globals.css'
import "slick-carousel/slick/slick.css";
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import "slick-carousel/slick/slick-theme.css";
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { PersistGate } from 'redux-persist/integration/react';
import { NextPage } from 'next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CookieConsent, { Cookies, getCookieConsentValue, resetCookieConsentValue } from 'react-cookie-consent';
import { BorderStyle } from '@mui/icons-material';
import { useRouter } from 'next/router';
import Head from "next/head";
import { ErrorBoundary } from 'react-error-boundary';
import CommonErrorPopup from '@/components/commonErrorPopup/commonErrorPopup';
import { ALLOWED_ENDPOINTS_ARRAY, ALLOWED_NOT_LOOGED_ENDPOINTS, AM_URL, CAPTCHA_INCLUDED_ENDPOINTS, DATA_DOG_APPLICATION_ID, DATA_DOG_CLIENT_TOKEN, DATA_DOG_ENV, DATA_DOG_SERVICE, DOCUMENT_HOST_NAME_PROD, DOCUSIGN_ENDPOINT, DOCUSING_STATUS_PAGE_PATH, GOOGLE_ANALYTICS_ID, GOOGLE_CAPTCHA_SITE_KEY_FORGEROCKV3, GTM_ID, IS_ONLY_FASTTRACK, JOURNEY_LOGIN_NORMAL, JOURNEY_LOGIN_UAEPASS, LOGIN_PATH, NOTIFICATIONS_HOST_NAME_PROD, PAYMENT_HOST_NAME_PROD, PROPERTIES_SEARCH_HOST_NAME_PROD, PROPERTY_HOST_NAME_PROD, SERVICE_REQUEST_HOST_NAME_PROD, USER_HOST_NAME_PROD, USE_GTM } from '@/config/constants';
import { userAgent } from 'next/server';
import { setForgerockConfig } from '@/services/forgeRockService.service';
import { UserManager } from '@forgerock/javascript-sdk';
import { updateUserDetail } from '@/reducers/user/userSlice';
import enCommonContent from '@/locales/en/commonContents';
import arCommonContent from '@/locales/ar/commonContents';
import TagManager from 'react-gtm-module'
import { addError, clearErrors } from '@/reducers/errorHandler/errorHandlerSlice';
import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs';
import { version } from '../../package.json'
import { pageview } from '@/lib/ga'
import { polyfillPromiseWithResolvers } from "@/utils/polyfilsResolver";
import DatadogUserWrapper from '@/hooks/useDatadogUser';
import { LogoutProvider } from '@/context/LogoutContext';

polyfillPromiseWithResolvers();

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};



const MyApp = ({ Component, pageProps }: AppPropsWithLayout) => {
  const getLayout = Component.getLayout ?? ((page) => page);
  const router: any = useRouter();
  const user = store.getState().user
  const { locale } = router;
  const tCommonContent: any = locale == 'en' ? enCommonContent : arCommonContent;
  const dataDogInitialisation = () => {
    datadogRum.init({
      applicationId:DATA_DOG_APPLICATION_ID,
      clientToken:DATA_DOG_CLIENT_TOKEN,
      site:'us3.datadoghq.com',
      service:DATA_DOG_SERVICE,
      allowedTracingUrls: [
        (url) => url.startsWith(USER_HOST_NAME_PROD),
        (url) => url.startsWith(PROPERTY_HOST_NAME_PROD),
        (url) => url.startsWith(SERVICE_REQUEST_HOST_NAME_PROD),
        (url) => url.startsWith(DOCUMENT_HOST_NAME_PROD),
        (url) => url.startsWith(PAYMENT_HOST_NAME_PROD),
        (url) => url.startsWith(NOTIFICATIONS_HOST_NAME_PROD),
        (url) => url.startsWith(PROPERTIES_SEARCH_HOST_NAME_PROD)
      ],
      env: DATA_DOG_ENV,
      // Specify a version number to identify the deployed version of your application in Datadog 
      version: version, 
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
    });
    datadogRum.setGlobalContext({
    product: 'live-aldar'
    })
    datadogLogs.init({
      clientToken: DATA_DOG_CLIENT_TOKEN,
      site: 'us3.datadoghq.com',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      service: DATA_DOG_SERVICE
    });
  }
  const checkSessionActive = async () => {
    if (ALLOWED_NOT_LOOGED_ENDPOINTS.indexOf(router.asPath.split('?')[0]) !== -1) {
      return
    }
    else if(pageProps?.statusCode === 404 ){
      return
    }
    try {
      setForgerockConfig(JOURNEY_LOGIN_UAEPASS)
      const userDataForgerockUAEPass: any = await UserManager.getCurrentUser()
      if (!userDataForgerockUAEPass) {
        store.dispatch(addError({
          type: 403,
          errorMessage: 'sessionExpiredMessage',
          buttonText: ""

        }))

      } else {
        store.dispatch(clearErrors())
      }
    } catch (e) {
      try {
        setForgerockConfig(JOURNEY_LOGIN_NORMAL)
        const userDataForgerockRegular: any = await UserManager.getCurrentUser()
        if (!userDataForgerockRegular) {
          store.dispatch(addError({
            type: 403,
            errorMessage: 'sessionExpiredMessage',
            buttonText: ""

          }))
        } else {
          store.dispatch(clearErrors())
        }
      } catch (e) {
        store.dispatch(addError({
          type: 403,
          errorMessage: 'sessionExpiredMessage',
          buttonText: ""

        }))
      }

    }

  }
  useEffect(() => {
    if (router.asPath.split('?')[0] === DOCUSING_STATUS_PAGE_PATH) {
      return
    }
    if (IS_ONLY_FASTTRACK && ALLOWED_ENDPOINTS_ARRAY.indexOf(router.asPath.split('?')[0]) === -1) {
      store.dispatch(updateUserDetail({
        userMessage: "useFasttrackLink"
      }))
      router.push(LOGIN_PATH)
      return
    }

    checkSessionActive()
  }, [router.asPath])
  useEffect(() => {
    if (USE_GTM) {
      const tagManagerArgs = {
        gtmId: GTM_ID
      }
      TagManager.initialize(tagManagerArgs)
    }
    dataDogInitialisation();
  }, [])

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      pageview(url);
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <LogoutProvider>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <DatadogUserWrapper />
          <Head>
            <title>{tCommonContent.portalTitle}</title>
            <link rel="icon" type="image/png" href="/live/en/iceridgeresources/images/favicon.jpg" />
            <meta name="viewport" content="initial-scale=1.0, width=device-width" />
            {/* Google Tag Manager */}
            {/* <script async src="https://www.googletagmanager.com/gtag/js?id=G-JZG9CPBZCK"></script>
            <script
              type="text/javascript"
              async
              dangerouslySetInnerHTML={{
                __html: `(console.log("gtm");window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);}; gtag('js', new Date()); gtag('config', 'G-JZG9CPBZCK');)`,
              }}
            /> */}
            {/* End Google Tag Manager  */}

            {/* OptanonConsentNoticeStart */}
            <script
              src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
              data-document-language="true"
              type="text/javascript"
              charSet="UTF-8"
              data-domain-script="018de707-3ce8-7c4c-9b7a-78114db7adfd"
            ></script>
            <script
              type="text/javascript"
              dangerouslySetInnerHTML={{
                __html: `
                  function OptanonWrapper() { }
                `,
              }}
            />
            {/* OptanonConsentNoticeEnd */}

            {/* Google Analyyics Start */}
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`}
            />
            <script
              dangerouslySetInnerHTML={{
                __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GOOGLE_ANALYTICS_ID}', {
                page_path: window.location.pathname,
              });
            `,
              }}
            />
            {/* Google Analyyics End */}
            {/*
              Defense-in-depth for the Lean SDK postMessage handler.
              The Lean loader registers a window 'message' listener with no
              origin/scheme validation; an attacker can postMessage a payload
              whose open_banking_redirect_url is a javascript:/data:/vbscript:
              URI and trigger DOM XSS via window.open(url, "_self").
              Registering this guard BEFORE the Lean loader script ensures it
              runs first (window-targeted listeners fire in registration order)
              so stopImmediatePropagation prevents the SDK handler from seeing
              the poisoned message.
            */}
            <script
              dangerouslySetInnerHTML={{
                __html: `
                  (function () {
                    var DANGEROUS_SCHEME_RE = /^\\s*(?:javascript|data|vbscript):/i;
                    function isDangerousString(v) {
                      return typeof v === 'string' && DANGEROUS_SCHEME_RE.test(v);
                    }
                    function hasDangerousUrl(value, depth) {
                      if (depth > 5) return false;
                      if (isDangerousString(value)) return true;
                      if (value && typeof value === 'object') {
                        for (var k in value) {
                          if (Object.prototype.hasOwnProperty.call(value, k)) {
                            if (hasDangerousUrl(value[k], depth + 1)) return true;
                          }
                        }
                      }
                      return false;
                    }
                    window.addEventListener('message', function (e) {
                      try {
                        if (e && e.data && hasDangerousUrl(e.data, 0)) {
                          e.stopImmediatePropagation();
                        }
                      } catch (err) { /* swallow */ }
                    }, true);
                  })();
                `,
              }}
            />
            <script src="https://cdn.leantech.me/link/loader/prod/ae/latest/lean-link-loader.min.js"></script>

          </Head>
            {getLayout(<Component {...pageProps} />)}
          {/* <CookieConsent
            location="bottom"
            buttonText={tCommonContent.cookiesAcceptButtonText}
            declineButtonText={tCommonContent.cookiesDeclineButtonText}
            onDecline={() => {

              window.open('https://www.aldar.com/en/privacy-policy', '_blank')
            }}
            enableDeclineButton
            cookieName="cookieAccepted"
            declineCookieValue={"false"}
            hideOnDecline={false}
            buttonClasses='cookieBannerAcceptButton'
            declineButtonClasses='cookieBannerDeclineButton'
            containerClasses={`cookieBannerOverlay ${locale === 'ar' ? 'cookieBannerOverlayAr' : 'cookieBannerOverlayEn'}`}
            flipButtons
          >
            {<>{tCommonContent.cookieBannerInnerText1}&nbsp;<u><a href="/en/privacy-policy" target="_blank">{tCommonContent.cookieBannerInnerText2}</a></u>{tCommonContent.cookieBannerInnerText3}</>}
          
          </CookieConsent> */}
        </PersistGate>
      </Provider>
    </LocalizationProvider >
    </LogoutProvider>
  )

}
export default MyApp
